import {
  AlertCircle,
  Building2,
  Calendar,
  Clock,
  FolderTree,
  Layers,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  RotateCcw,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/common/ui/avatar";
import { Badge } from "@/common/ui/badge";
import { Button, buttonVariants } from "@/common/ui/button";
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "@/common/ui/empty";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/common/ui/sheet";
import { Skeleton } from "@/common/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/ui/tabs";
import { PermissionGate } from "@/lib/permissions";
import { usePermission } from "@/lib/use-permission";
import { cn } from "@/lib/utils";

import { resendInvitation, revokeInvitation } from "../api/organization-api";
import { formatInvitationExpiry } from "../invitation-expiry";
import { orgUnitTypeLabel } from "../org-unit-types";
import type { Invitation, Member, OrgUnit, SessionMembershipRole } from "../types";
import { EditMemberRolesModal } from "./EditMemberRolesModal";
import { InviteMemberModal } from "./InviteMemberModal";
import { RemoveMemberDialog } from "./RemoveMemberDialog";

const typeIcons: Record<string, typeof Building2> = {
  enterprise: Building2,
  division: Layers,
  department: FolderTree,
  team: Users,
  location: MapPin,
};

const statusStyles: Record<string, { bg: string; text: string }> = {
  active: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" },
  inactive: { bg: "bg-zinc-500/10", text: "text-zinc-600 dark:text-zinc-400" },
  pending: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400" },
};

const invitationStatusStyles: Record<string, { bg: string; text: string; icon: typeof Clock }> = {
  pending: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", icon: Clock },
  expired: {
    bg: "bg-red-500/10",
    text: "text-red-600 dark:text-red-400",
    icon: AlertCircle,
  },
  revoked: { bg: "bg-zinc-500/10", text: "text-zinc-600 dark:text-zinc-400", icon: X },
  accepted: {
    bg: "bg-emerald-500/10",
    text: "text-emerald-600 dark:text-emerald-400",
    icon: Users,
  },
};

const roleColors: Record<string, string> = {
  admin: "bg-red-500/10 text-red-600 border-red-500/30 dark:text-red-400",
  member: "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400",
  viewer: "bg-zinc-500/10 text-zinc-600 border-zinc-500/30 dark:text-zinc-400",
};

interface OrgUnitDetailPanelProps {
  node: OrgUnit | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: Member[];
  invitations: Invitation[];
  isLoading?: boolean;
  detailError?: string | null;
  availableRoles: SessionMembershipRole[];
  onDataChanged: () => void;
}

export function OrgUnitDetailPanel({
  node,
  open,
  onOpenChange,
  members,
  invitations,
  isLoading = false,
  detailError = null,
  availableRoles,
  onDataChanged,
}: OrgUnitDetailPanelProps): JSX.Element | null {
  const canInvite = usePermission("members:invite");
  const canEditRoles = usePermission("members:update_roles");
  const canRemove = usePermission("members:remove");

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [removingMember, setRemovingMember] = useState<Member | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Invitation | null>(null);
  // Track pending invitations by id so concurrent resends don't stomp each other's
  // disabled/spinner state (a single shared id would let one request's "finally" clear
  // the UI lock for another request still in flight).
  const [pendingInvitationIds, setPendingInvitationIds] = useState<Set<string>>(
    () => new Set<string>()
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Reset panel-local state when the selected node changes or the sheet closes so
  // dialogs / errors / pending-action state from one node don't bleed into the next
  // (the parent OrganizationPage can switch nodes while keeping the panel open).
  const nodeId = node?.id ?? null;
  useEffect(() => {
    setInviteModalOpen(false);
    setEditingMember(null);
    setRemovingMember(null);
    setRevokeTarget(null);
    setPendingInvitationIds(new Set<string>());
    setActionError(null);
    setIsRevoking(false);
  }, [nodeId, open]);

  if (!node) return null;

  const Icon = typeIcons[node.org_unit_type] ?? Building2;
  const statusStyle = statusStyles[node.status] ?? statusStyles["active"]!;

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const pendingInvitations = invitations.filter((inv) => inv.status === "pending");
  const otherInvitations = invitations.filter((inv) => inv.status !== "pending");

  const markInvitationPending = (id: string, pending: boolean): void => {
    setPendingInvitationIds((prev) => {
      const next = new Set(prev);
      if (pending) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleResend = async (invitation: Invitation): Promise<void> => {
    setActionError(null);
    markInvitationPending(invitation.id, true);
    try {
      await resendInvitation(invitation.id);
      // The refetched invitations prop reflects the new expires_at inline on the card —
      // no separate notice banner needed (the banner had no clear signal and stuck around).
      onDataChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setActionError(
        message.includes("Forbidden") || message.includes("permission")
          ? "You don't have permission to resend this invitation."
          : "Failed to resend invitation. Please try again."
      );
    } finally {
      markInvitationPending(invitation.id, false);
    }
  };

  const handleConfirmRevoke = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    if (!revokeTarget) return;
    const targetId = revokeTarget.id;
    setActionError(null);
    setIsRevoking(true);
    markInvitationPending(targetId, true);
    try {
      await revokeInvitation(targetId);
      setRevokeTarget(null);
      onDataChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setActionError(
        message.includes("Forbidden") || message.includes("permission")
          ? "You don't have permission to revoke this invitation."
          : "Failed to revoke invitation. Please try again."
      );
    } finally {
      setIsRevoking(false);
      markInvitationPending(targetId, false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader className="border-b pb-4">
            <div className="flex items-center gap-3">
              <div className={cn("rounded-lg p-2", statusStyle.bg)}>
                <Icon className={cn("h-5 w-5", statusStyle.text)} />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="truncate text-left">{node.name}</SheetTitle>
                <SheetDescription className="text-left">/{node.slug}</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Type</p>
                <Badge variant="outline">{orgUnitTypeLabel(node.org_unit_type)}</Badge>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge
                  variant="outline"
                  className={cn("capitalize border-0", statusStyle.bg, statusStyle.text)}
                >
                  {node.status}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Created</p>
                <div className="flex items-center gap-1.5 text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatDate(node.created_at)}
                </div>
              </div>
            </div>

            <Tabs defaultValue="members" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="members" className="flex-1">
                  Members ({members.length})
                </TabsTrigger>
                <TabsTrigger value="invitations" className="flex-1">
                  Invitations ({invitations.length})
                </TabsTrigger>
              </TabsList>

              {detailError && (
                <div
                  data-testid="detail-error"
                  className="mt-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {detailError}
                </div>
              )}

              {actionError && (
                <div
                  data-testid="member-action-error"
                  className="mt-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{actionError}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setActionError(null)}
                    aria-label="Dismiss error"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}

              <TabsContent value="members" className="mt-4 space-y-2">
                {isLoading ? (
                  <div data-testid="members-loading" className="space-y-2">
                    <Skeleton className="h-16 w-full rounded-lg" />
                    <Skeleton className="h-16 w-full rounded-lg" />
                  </div>
                ) : members.length === 0 ? (
                  <Empty className="py-8">
                    <EmptyMedia variant="icon">
                      <Users className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyTitle>No members</EmptyTitle>
                    <EmptyDescription>Invite team members to get started</EmptyDescription>
                    <PermissionGate permission="members:invite">
                      <Button size="sm" onClick={() => setInviteModalOpen(true)}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Invite Member
                      </Button>
                    </PermissionGate>
                  </Empty>
                ) : (
                  members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 rounded-lg border bg-card p-3"
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs">
                          {member.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{member.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                      </div>
                      <div className="flex flex-wrap justify-end gap-1">
                        {member.roles.map((role) => (
                          <Badge
                            key={role}
                            variant="outline"
                            className={cn("px-1.5 py-0 text-[10px]", roleColors[role])}
                          >
                            {role}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          data-testid={`edit-member-${member.id}`}
                          aria-label={`Edit roles for ${member.email}`}
                          disabled={!canEditRoles}
                          title={
                            canEditRoles ? undefined : "You don't have permission to edit roles"
                          }
                          onClick={() => setEditingMember(member)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          data-testid={`remove-member-${member.id}`}
                          aria-label={`Remove ${member.email}`}
                          disabled={!canRemove}
                          title={
                            canRemove ? undefined : "You don't have permission to remove members"
                          }
                          onClick={() => setRemovingMember(member)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="invitations" className="mt-4 space-y-4">
                <PermissionGate permission="members:invite">
                  <Button size="sm" onClick={() => setInviteModalOpen(true)} className="w-full">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Member
                  </Button>
                </PermissionGate>

                {isLoading ? (
                  <div data-testid="invitations-loading" className="space-y-2">
                    <Skeleton className="h-16 w-full rounded-lg" />
                    <Skeleton className="h-16 w-full rounded-lg" />
                  </div>
                ) : invitations.length === 0 ? (
                  <Empty className="py-8">
                    <EmptyMedia variant="icon">
                      <Mail className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyTitle>No invitations</EmptyTitle>
                    <EmptyDescription>Send invitations to add team members</EmptyDescription>
                  </Empty>
                ) : (
                  <>
                    {pendingInvitations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          Pending
                        </p>
                        {pendingInvitations.map((inv) => (
                          <InvitationCard
                            key={inv.id}
                            invitation={inv}
                            showActions={canInvite}
                            isActionPending={pendingInvitationIds.has(inv.id)}
                            onResend={() => handleResend(inv)}
                            onRevoke={() => {
                              setActionError(null);
                              setRevokeTarget(inv);
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {otherInvitations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          History
                        </p>
                        {otherInvitations.map((inv) => (
                          <InvitationCard key={inv.id} invitation={inv} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      <InviteMemberModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        orgUnitId={node.id}
        orgUnitName={node.name}
        availableRoles={availableRoles}
        onInviteSent={onDataChanged}
      />

      {editingMember && (
        <EditMemberRolesModal
          open={editingMember !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setEditingMember(null);
          }}
          member={editingMember}
          availableRoles={availableRoles}
          onSaved={onDataChanged}
        />
      )}

      {removingMember && (
        <RemoveMemberDialog
          open={removingMember !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setRemovingMember(null);
          }}
          member={removingMember}
          onRemoved={onDataChanged}
        />
      )}

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(nextOpen) => {
          if (isRevoking) return;
          if (!nextOpen) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget
                ? `${revokeTarget.email} will no longer be able to accept this invitation. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-cancel" disabled={isRevoking}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="revoke-confirm"
              className={cn(buttonVariants({ variant: "destructive" }))}
              disabled={isRevoking}
              onClick={handleConfirmRevoke}
            >
              {isRevoking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Revoke"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface InvitationCardProps {
  invitation: Invitation;
  showActions?: boolean;
  isActionPending?: boolean;
  onResend?: () => void;
  onRevoke?: () => void;
}

function InvitationCard({
  invitation,
  showActions = false,
  isActionPending = false,
  onResend,
  onRevoke,
}: InvitationCardProps): JSX.Element {
  const style = invitationStatusStyles[invitation.status] ?? invitationStatusStyles["pending"]!;
  const StatusIcon = style.icon;

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-card p-3">
      <div className="flex items-start gap-3">
        <div className={cn("rounded-md p-1.5", style.bg)}>
          <StatusIcon className={cn("h-4 w-4", style.text)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{invitation.email}</p>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex gap-1">
              {invitation.roles.map((role) => (
                <Badge
                  key={role}
                  variant="outline"
                  className={cn("px-1.5 py-0 text-[10px]", roleColors[role])}
                >
                  {role}
                </Badge>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">
              {invitation.status === "pending"
                ? formatInvitationExpiry(invitation.expires_at)
                : invitation.status === "expired"
                  ? "Expired"
                  : invitation.status === "revoked"
                    ? "Revoked"
                    : "Accepted"}
            </span>
          </div>
        </div>
        {showActions && invitation.status === "pending" && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              data-testid={`resend-invitation-${invitation.id}`}
              aria-label={`Resend invitation to ${invitation.email}`}
              disabled={isActionPending}
              onClick={onResend}
            >
              {isActionPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              data-testid={`revoke-invitation-${invitation.id}`}
              aria-label={`Revoke invitation to ${invitation.email}`}
              disabled={isActionPending}
              onClick={onRevoke}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
